import { Box, Button, Flex, Text, Textarea } from "@chakra-ui/react";
import { AgentTaskProgress } from "@fencyai/react";
import { Fragment, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FENCY_DEFAULT_MODEL } from "./fencySetup";
import { useExploreMemoriesChat } from "./useExploreMemoriesChat";

type ExploreMemoriesChatProps = {
  fetchCreateExploreMemoriesClientToken: (conversationId: string) => Promise<{ clientToken: string }>;
  fetchFencyFileDownloadClientToken: (agentTaskFileId: string) => Promise<{ clientToken: string }>;
};

export const ExploreMemoriesChat = ({
  fetchCreateExploreMemoriesClientToken,
  fetchFencyFileDownloadClientToken,
}: ExploreMemoriesChatProps) => {
  const { t } = useTranslation();
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { userMessages, agentTasks, prompt, setPrompt, isLoading, handleSend } = useExploreMemoriesChat({
    model: FENCY_DEFAULT_MODEL,
    fetchCreateExploreMemoriesClientToken,
    fetchFencyFileDownloadClientToken,
  });

  useEffect(() => {
    if (userMessages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [userMessages, agentTasks]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const isDisabled = isLoading;

  return (
    <Flex flex={1} flexDirection="column" minWidth={0} minH={0} overflow="hidden">
      <Box
        ref={messagesContainerRef}
        flex={1}
        minHeight={0}
        overflowY="auto"
        py="4"
        px="2"
        display="flex"
        flexDirection="column"
        gap="3"
      >
        {userMessages.length === 0 && (
          <Flex flex={1} alignItems="center" justifyContent="center" color="smMuted" fontSize="sm" textAlign="center">
            <Text>
              {t(
                "Ask a question and the agent will search indexed contract documents — running queries, code, and citing what it finds."
              )}
            </Text>
          </Flex>
        )}
        {userMessages.map((content, i) => (
          <Fragment key={`msg-${i}`}>
            <Flex justifyContent="flex-end">
              <Box
                maxWidth="75%"
                px="4"
                py="2"
                borderRadius="16px 16px 4px 16px"
                backgroundColor="smPrimary"
                color="white"
                fontSize="sm"
                lineHeight="tall"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {content}
              </Box>
            </Flex>
            {agentTasks[i] && (
              <Flex justifyContent="flex-start">
                <Box maxWidth="100%" width="100%">
                  <AgentTaskProgress agentTask={agentTasks[i]} language="no" />
                </Box>
              </Flex>
            )}
          </Fragment>
        ))}
      </Box>

      <Flex
        pt="3"
        pb="4"
        borderTopWidth="1px"
        borderColor="smBorder"
        gap="2"
        alignItems="flex-end"
        flexShrink={0}
        flexDirection="column"
      >
        <Flex gap="2" alignItems="flex-end" width="100%">
          <Textarea
            placeholder={t("Search contract documents…") ?? ""}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            flex={1}
            resize="none"
            isDisabled={isDisabled}
          />
          <Button
            onClick={() => void handleSend()}
            colorScheme="teal"
            flexShrink={0}
            height="56px"
            px="5"
            isLoading={isLoading}
            isDisabled={isDisabled || !prompt.trim()}
          >
            {t("Send")}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
